import type { FC, ReactNode } from 'react'
import type { NewsPostSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Crest } from '@/components/generals/crest'

import { newsArticlePath } from '@/constants/navigation'

interface NewsCardProps {
    post: NewsPostSummary
    variant?: 'default' | 'featured'
    headingLevel?: 'h2' | 'h3' | 'h4'
}

/**
 * A news story card. Every database text value carries its data-mdlfy-cms tag
 * so it can be edited straight from the preview.
 */
export const NewsCard: FC<NewsCardProps> = ({ post, variant = 'default', headingLevel = 'h3' }): ReactNode => {
    const Heading = headingLevel
    const featured = variant === 'featured'

    return (
        <article
            className={cn(
                'group relative flex h-full flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-border transition-shadow duration-300 hover:shadow-xl hover:shadow-navy/10 focus-within:ring-2 focus-within:ring-navy',
                featured && 'md:flex-row'
            )}
        >
            <div
                className={cn(
                    'relative aspect-[16/10] shrink-0 overflow-hidden bg-sand',
                    featured && 'md:aspect-auto md:w-1/2'
                )}
            >
                {post.coverImage ? (
                    <img
                        src={post.coverImage}
                        alt={post.coverImageAlt ?? ''}
                        loading='lazy'
                        decoding='async'
                        className='absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                    />
                ) : (
                    <div className='absolute inset-0 flex items-center justify-center bg-navy'>
                        <Crest className='h-20 w-auto opacity-80' />
                    </div>
                )}
            </div>

            <div className={cn('flex flex-1 flex-col p-6', featured && 'md:p-8')}>
                <div className='flex flex-wrap items-center gap-x-3 gap-y-2 text-sm'>
                    {post.category && (
                        <span className='rounded-full bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wider text-navy'>
                            <span data-mdlfy-cms={`news_posts:${post.id}:category`}>{post.category}</span>
                        </span>
                    )}

                    <time dateTime={post.publishedAt} className='text-muted-foreground'>
                        {post.publishedLabel}
                    </time>
                </div>

                <Heading
                    className={cn(
                        'mt-4 text-balance font-heading font-semibold leading-snug text-navy',
                        featured ? 'text-2xl sm:text-[1.75rem]' : 'text-xl'
                    )}
                >
                    <Link
                        href={newsArticlePath(post.slug)}
                        className='outline-none after:absolute after:inset-0 after:content-[""] group-hover:underline group-hover:decoration-gold group-hover:decoration-2 group-hover:underline-offset-4'
                    >
                        <span data-mdlfy-cms={`news_posts:${post.id}:title`}>{post.title}</span>
                    </Link>
                </Heading>

                {post.excerpt && (
                    <p
                        data-mdlfy-cms={`news_posts:${post.id}:excerpt`}
                        className={cn('mt-3 text-muted-foreground', featured ? 'line-clamp-4 text-lg' : 'line-clamp-3')}
                    >
                        {post.excerpt}
                    </p>
                )}

                <span className='mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-navy'>
                    Read the story

                    <ArrowRight aria-hidden='true' className='size-4 text-gold-deep transition-transform group-hover:translate-x-1' />
                </span>
            </div>
        </article>
    )
}
